// This class manages the tiptap editor and the hocuspocus provider for read-only documents
// A fork of Editor.js

import { Editor as TipTapEditor } from "@tiptap/core";
import { serializeSliceToMarkdown } from "./clipboardTextSerializer";
import Collaboration from "@tiptap/extension-collaboration";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import { Document as TipTapDocument } from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Heading from "@tiptap/extension-heading";
import Underline from "@tiptap/extension-underline";
import Hardbreak from "@tiptap/extension-hard-break";
import Strike from "@tiptap/extension-strike";
import { ThemeAwareHighlight } from "./tipTapExtensions/themeAwareHighlight";
import ListItem from "@tiptap/extension-list-item";
import { createTaskListExtensions } from "./tipTapExtensions/taskListExtensions";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import Inserted from "@sudowrite/extension-inserted";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import History from "@tiptap/extension-history";
import Typography from "@tiptap/extension-typography";
import Annotation from "../../../src/shared/annotation";

import * as Y from "yjs";
import { HocuspocusProvider } from "@hocuspocus/provider";
import { Extension } from "@tiptap/core";
import * as Sentry from "@sentry/react";
import { reportContentError } from "./contentErrorReporter";
import { IndexeddbPersistence } from "y-indexeddb";
import TrackChangeExtension from "../../../src/shared/trackChanges";

import { createReadOnlyPlaceholder } from "./tipTapExtensions/readOnlyPlaceholder";
import { Plugin } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import SearchAndReplace from "./tipTapExtensions/searchAndReplace";
import { PasteFromTextBoxExtension } from "./tipTapExtensions/pasteFromTextBox";
import { countWords, keepWordsFromEnd } from "../Utils";

async function checkIfSharedDocumentIsRevoked(sharedDoc) {
  const response = await fetch(`/api/documents/${sharedDoc.shortId}/feedback`);
  if (response.status === 404) {
    return true;
  } else {
    return false;
  }
}

function applyDecoration(state, view, fromPos, toPos) {
  console.log("applyDecoration", fromPos, toPos);
  let transaction = state.tr.setMeta("highlightDecoration", { fromPos: fromPos, toPos: toPos });
  // transaction = transaction.step(new ReplaceStep(fromPos, fromPos, state.tr.doc.slice(fromPos, fromPos)));
  view.dispatch(transaction);
}

// Tiptap extension that wraps the prose mirror highlight decoration plugin
const highlightExtension = Extension.create({
  addProseMirrorPlugins() {
    return [highlightDecoration];
  },
});

const highlightDecoration = new Plugin({
  state: {
    init() {
      // No decorations set by default
      return DecorationSet.empty;
    },
    apply(tr, value) {
      const highlightMeta = tr.getMeta("highlightDecoration");
      if (highlightMeta?.clear) {
        return DecorationSet.empty;
      }
      if (highlightMeta) {
        const { fromPos, toPos } = highlightMeta;
        return DecorationSet.create(tr.doc, [
          Decoration.inline(fromPos, toPos, {
            class: "sudo-agent-edit-highlight",
          }),
        ]);
      } else {
        // map "other" changes so our decoration "stays put"
        // (e.g. user is typing so decoration's pos must change)
        return value.map(tr.mapping, tr.doc);
      }
    },
  },
  props: {
    decorations(state) {
      return highlightDecoration.getState(state);
    },
  },
});

const DisableMetaEnter = Extension.create({
  addKeyboardShortcuts() {
    return {
      "Meta-Enter": () => true,
    };
  },
});

/**
 * @typedef {Object} EditorOptions
 * @property {string} documentId document id
 */
export default class ReadOnlyEditor {
  constructor({ documentId, editorHandlers, user, showError, sharedDoc }) {
    // console.log("ReadOnlyEditor constructor", documentId);
    this.ydoc = new Y.Doc();
    this.ydocLocalStorage = new IndexeddbPersistence(documentId, this.ydoc);
    this.ydocLocalStorage.on("synced", () => {
      // console.log("loaded content from local storage");
    });
    this.documentId = documentId;
    this.user = user;
    this.disconnectTimeout = null;
    this.inited = false;
    this.annotationConfig = editorHandlers?.annotations;
    this.onSelectionUpdate = typeof editorHandlers?.onSelectionUpdate === "function" ? editorHandlers.onSelectionUpdate : () => {};
    this.showError = showError;
    this.sharedDoc = sharedDoc;
    this.onEditorRebuild = null;
    this._fallbackTimeout = null;
    this._fallbackApplied = false;
  }

  // Initializes the editor and provider
  async init() {
    const token = async () => {
      // console.log("[hocuspocus] getting firebase token");
      if (!this.user) {
        return "";
      }
      return await this.user.getIdToken(false);
    }; // gets the id token from firebase
    const hostInstance = this;

    const params = {
      url: process.env.REACT_APP_WEBSOCKET_URL,
      name: this.documentId,
      document: this.ydoc,
      token,
      onOpen() {
        clearTimeout(hostInstance.disconnectTimeout);
        Sentry.addBreadcrumb({
          message: `[hocuspocus] onOpen — connection established`,
          data: { documentId: hostInstance.documentId },
        });
        // Restore editability in case the 30s disconnect timer already locked the editor.
        if (hostInstance.tiptap) {
          hostInstance.tiptap.setEditable(true);
        }
      },
      onAuthenticated() {
        // …
        // console.log("[hocuspocus] onAuthenticated");
      },
      onAuthenticationFailed({ reason }) {
        // …
        // console.log("[hocuspocus] onAuthenticationFailed reason: " + reason);
        checkIfSharedDocumentIsRevoked(hostInstance.sharedDoc).then((isRevoked) => {
          if (!hostInstance) {
            Sentry.captureMessage("HocusPocus Auth Failed in Shared Document: " + reason, {
              userId: "No hostInstance",
              documentId: "No hostInstance",
              shortId: "No hostInstance",
              title: "No hostInstance",
              documentHasBeenRevoked: isRevoked,
            });
          } else {
            Sentry.captureMessage("HocusPocus Auth Failed in Shared Document: " + reason, {
              userId: hostInstance.user?.uid,
              documentId: hostInstance.documentId,
              shortId: hostInstance.sharedDoc?.shortId,
              title: hostInstance.sharedDoc?.title,
              documentHasBeenRevoked: isRevoked,
            });
          }

          hostInstance.showError("Could not connect to server. Comments may not be saved.");
        });
      },
      onStatus: ({ status }) => {
        // …
      },
      onMessage: ({ event, message }) => {
        // …
      },
      onOutgoingMessage: ({ message }) => {
        // …
      },
      onClose: ({ event }) => {
        Sentry.addBreadcrumb({
          message: `[hocuspocus] onClose`,
          data: { documentId: hostInstance.documentId, code: event?.code, reason: event?.reason },
        });
      },
      onDisconnect: ({ event }) => {
        Sentry.addBreadcrumb({
          message: `[hocuspocus] onDisconnect`,
          data: { documentId: hostInstance.documentId, code: event?.code, reason: event?.reason },
        });
        hostInstance.disconnectTimeout = setTimeout(() => {
          Sentry.captureMessage("HocusPocus Unable to reconnect after 30 seconds.", {
            tags: {
              documentId: hostInstance.documentId,
            },
          });
          hostInstance?.showError("Disconnected from server.");
          hostInstance.tiptap.setEditable(false);
          Sentry.addBreadcrumb({
            message: `[hocuspocus] Editor locked after 30s disconnect timeout`,
            data: { documentId: hostInstance.documentId },
          });
        }, 30000);
      },
      onDestroy() {
        // …
        // console.log("[hocuspocus] onDestroy");
        clearTimeout(hostInstance.disconnectTimeout);
      },
      onAwarenessUpdate: ({ added, updated, removed }) => {
        // …
      },
      onAwarenessChange: ({ states }) => {
        // …
      },
      onStateless: ({ payload }) => {
        // ...
        // the provider can also send a custom message to the server: provider.sendStateless('any string payload')
      },
    };

    // Construct the HocuspocusProvider instance
    this.provider = new HocuspocusProvider(params);

    this.tiptap = new TipTapEditor({
      enableContentCheck: true,
      onContentError: ({ error }) => {
        reportContentError(error, this.documentId);
      },
      extensions: [
        Collaboration.configure({
          document: this.ydoc,
        }),
        TipTapDocument,
        Paragraph,
        Typography,
        Text,
        Bold,
        Italic,
        Heading,
        Underline,
        Hardbreak,
        Strike,
        ThemeAwareHighlight.configure({
          multicolor: true,
        }),
        BulletList,
        OrderedList,
        ListItem,
        ...createTaskListExtensions({ canWrite: false }),
        Inserted,
        Link,
        Image.configure({
          inline: true,
        }),
        History,
        DisableMetaEnter,
        TrackChangeExtension.configure({
          enabled: false,
          dataOpUserId: "", // set the op userId
          dataOpUserNickname: "", // set the op user nickname
          onStatusChange(status) {
            // myTrackChangeEnabled = status
          },
          onStrayMarksCleaned: ({ count, staleSessionIds, currentSessionId }) => {
            Sentry.captureMessage("Quick Edit cleaned stray tracked-change marks", {
              level: "warning",
              tags: {
                feature: "trackChanges_strayCleanup",
                documentId: this.documentId,
                editor: "ReadOnlyEditor",
              },
              extra: {
                count,
                staleSessionIds,
                currentSessionId,
              },
            });
          },
        }),
        createReadOnlyPlaceholder(),
        highlightExtension,
        SearchAndReplace.configure(),
        PasteFromTextBoxExtension.configure(),
        Annotation.configure(this.annotationConfig),
      ],
      editable: false,
      editorProps: {
        attributes: {
          // Prevent iPad Safari from summoning the on-screen keyboard when the
          // reader taps the editor. contenteditable="false" alone is unreliable.
          inputmode: "none",
        },
        // Same markdown-flavored text/plain copy as the main editor
        clipboardTextSerializer: (slice, view) => serializeSliceToMarkdown(slice, view.state.selection.$from),
      },
      onSelectionUpdate: this.onSelectionUpdate,
    });

    this.inited = true;

    // If the Yjs document has no content after IndexedDB and HocusPocus sync,
    // rebuild the editor without the Collaboration extension using the API-provided
    // content directly. This avoids Yjs merge issues and preserves text selection.
    if (this.sharedDoc?.text?.default) {
      const attemptFallback = () => {
        if (this._fallbackApplied) return;
        const ydocContent = this.ydoc.getXmlFragment('default');
        if (ydocContent.length === 0) {
          this._rebuildEditorWithStaticContent();
        }
      };

      const afterIndexedDB = () => {
        const ydocContent = this.ydoc.getXmlFragment('default');
        // If IndexedDB already had cached content, no fallback needed
        if (ydocContent.length > 0) return;

        // If HocusPocus already synced (and fragment is still empty), fall back now
        if (this.provider.synced) {
          attemptFallback();
          return;
        }

        // Give HocusPocus a grace period to sync before falling back
        const onProviderSync = () => {
          clearTimeout(this._fallbackTimeout);
          attemptFallback();
        };
        this.provider.on('synced', onProviderSync);
        this._fallbackTimeout = setTimeout(() => {
          this.provider.off('synced', onProviderSync);
          attemptFallback();
        }, 6000);
      };

      // Wait for IndexedDB persistence to finish loading cached state
      if (this.ydocLocalStorage.synced) {
        afterIndexedDB();
      } else {
        this._fallbackTimeout = setTimeout(() => {
          afterIndexedDB();
        }, 4000);
        this.ydocLocalStorage.once('synced', () => {
          clearTimeout(this._fallbackTimeout);
          afterIndexedDB();
        });
      }
    }
  }

  /**
   * Replaces the Collaboration-backed editor with a static read-only editor
   * that renders content from the API response. Called when HocusPocus sync
   * fails and the Yjs document remains empty.
   */
  _rebuildEditorWithStaticContent() {
    if (this._fallbackApplied) return;
    this._fallbackApplied = true;

    const oldTiptap = this.tiptap;
    const oldProvider = this.provider;

    // Build new editor without Collaboration — use static content from API
    this.tiptap = new TipTapEditor({
      enableContentCheck: true,
      onContentError: ({ error }) => {
        reportContentError(error, this.documentId);
      },
      extensions: [
        TipTapDocument,
        Paragraph,
        Typography,
        Text,
        Bold,
        Italic,
        Heading,
        Underline,
        Hardbreak,
        Strike,
        ThemeAwareHighlight.configure({
          multicolor: true,
        }),
        BulletList,
        ListItem,
        ...createTaskListExtensions({ canWrite: false }),
        Inserted,
        Link,
        Image.configure({
          inline: true,
        }),
        History,
        DisableMetaEnter,
        TrackChangeExtension.configure({
          enabled: false,
          dataOpUserId: "",
          dataOpUserNickname: "",
          onStatusChange(status) {},
          onStrayMarksCleaned: ({ count, staleSessionIds, currentSessionId }) => {
            Sentry.captureMessage("Quick Edit cleaned stray tracked-change marks", {
              level: "warning",
              tags: {
                feature: "trackChanges_strayCleanup",
                documentId: this.documentId,
                editor: "ReadOnlyEditor",
              },
              extra: { count, staleSessionIds, currentSessionId },
            });
          },
        }),
        createReadOnlyPlaceholder(),
        highlightExtension,
        SearchAndReplace.configure(),
        PasteFromTextBoxExtension.configure(),
        Annotation.configure(this.annotationConfig),
      ],
      content: this.sharedDoc.text.default,
      editable: false,
      editorProps: {
        attributes: {
          inputmode: "none",
        },
        // Same markdown-flavored text/plain copy as the main editor
        clipboardTextSerializer: (slice, view) => serializeSliceToMarkdown(slice, view.state.selection.$from),
      },
      onSelectionUpdate: this.onSelectionUpdate,
    });

    // Notify React before tearing down old editor so there's no gap
    if (this.onEditorRebuild) {
      this.onEditorRebuild(this.tiptap);
    }

    // Now tear down old live-sync infrastructure
    oldProvider.destroy();
    this.provider = null;
    oldTiptap.destroy();
  }

  clearAllHighlights() {
    applyDecoration(this.tiptap.state, this.tiptap.view, 0, 0);
  }

  getHighlightedText() {
    const highlights = this.findHighlights();

    // if nothing is highlighted...
    if (!highlights) {
      // return an empty string and the current cursor position
      const currentPos = this.lastCursorPosition || 0;

      return {
        text: "",
        from: currentPos,
        to: currentPos,
      };
    }

    const highlightRange = highlights[0];

    // const firstRange = ranges[0];
    const text = this.tiptap.view.state.doc.textBetween(highlightRange.from, highlightRange.to);
    return {
      text,
      from: highlightRange.from,
      to: highlightRange.to,
    };
  }

  getPrecedingText(maxWords = 2000) {
    const currentPos = this.lastCursorPosition || 0;
    const allText = this.tiptap.view.state.doc.textBetween(0, currentPos);

    if (countWords(allText) <= maxWords) {
      return allText;
    }

    return keepWordsFromEnd(maxWords, allText);
  }

  saveSelection() {
    this.savedSelection = this.tiptap.state.selection;
  }

  restoreSelection() {
    this.tiptap.commands.focus(0);
    if (this.savedSelection) {
      this.tiptap.commands.setTextSelection({ from: this.savedSelection.from, to: this.savedSelection.to });
    }
  }

  setLastCursorPosition(pos) {
    this.lastCursorPosition = pos ?? this.tiptap.state.selection.head;
  }

  highlight(from, to, save = false) {
    const { state, view } = this.tiptap;
    let transaction = state.tr.setMeta("highlightDecoration", { fromPos: from, toPos: to });
    view.dispatch(transaction);

    if (save) {
      this.highlightRange = {
        from: from,
        to: to,
      };
    }

    this.clearSelection();
  }

  findHighlights() {
    const { state } = this.tiptap; // PM EditorState object
    const decorationSet = highlightDecoration.getState(state);

    const decorations = decorationSet.find();
    const highlights = decorations
      .filter((decoration) => decoration.type.attrs.class === "sudo-agent-edit-highlight")
      .map((decoration) => {
        return { from: decoration.from, to: decoration.to };
      });
    if (highlights.length > 0) {
      return highlights;
    } else {
      return null;
    }
  }

  getHighlightCoords() {
    const highlights = this.findHighlights();
    let from, to;

    if (!highlights) {
      // use last cursor position if nothing is highlighted
      from = this.lastCursorPosition || 0;
      to = this.lastCursorPosition || 0;
    } else {
      // get the range of the first highlight
      from = highlights[0].from;
      to = highlights[0].to;
    }

    const { view } = this.tiptap;

    // Get the coordinates for the start and end of the highlight
    let startCoords;
    let endCoords;

    try {
      startCoords = view.coordsAtPos(from);
      endCoords = view.coordsAtPos(to);
    } catch (e) {
      const err = new Error(`Editor cannot get coordinates for highlights: ${e.message}`);
      Sentry.captureException(err, {
        tags: {
          documentId: this.documentId,
          user: this.user,
          highlights,
          from,
          to,
        },
      });
      return null;
    }

    // bounding pixel positions of the highlight
    const boundingRect = {
      left: startCoords.left,
      top: startCoords.top,
      right: endCoords.right,
      bottom: endCoords.bottom,
    };

    return boundingRect;
  }

  getPosFromNode(DOMnode) {
    const { view } = this.tiptap; // PM EditorView object
    const from = view.posAtDOM(DOMnode, 0); // Offset 0 for the start of the node
    // const resolvedPos = view.state.doc.resolve(from); // Resolve the position to a node
    // const to = resolvedPos.end(); // Get the end position of the node
    const to = from + DOMnode.textContent.length;
    return { from, to };
  }

  clearSelection() {
    this.tiptap.commands.setTextSelection(0); // from: 0, to: 0
  }

  destroy() {
    clearTimeout(this._fallbackTimeout);

    // Disconnect the WebSocket provider before teardown. Do not reject tracked
    // changes during destroy: that mutates local Yjs content and can remove
    // insertion-marked text while navigating between documents.
    try {
      this.provider?.disconnect?.();
    } catch (_err) {
      // never block destroy on disconnect failures
    }

    let tiptap = false;
    let provider = false;
    // console.log("TipTap Editor getting wrecked");
    if (this.tiptap) {
      this.tiptap.destroy();
      // console.log("TipTap Instance destroyed");
      tiptap = true;
    }
    if (this.provider) {
      this.provider.destroy();
      // console.log("HocusPocus Provider destroyed");
      provider = true;
    }
    if (this.ydoc) {
      this.ydoc.destroy();
      // console.log("YDoc destroyed");
    }
    // console.log("TipTap Editor destroyed");
    Sentry.addBreadcrumb({
      message: `TipTap Editor destroyed: tiptap: ${tiptap}, provider: ${provider}`,
    });
    clearInterval(this.savedIndicatorInterval);
  }
}
